import './index.css'
import get from 'lodash/get'
import * as React from 'react'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import validation from 'react-validation-mixin'
import strategy from 'react-validatorjs-strategy'
import type { PasswordStrength } from 'types/business'
import type { LoginDetails, Settings } from 'types/user'
import getReferrer from '@disco/utils/getReferrer'
import { checkPasswordSecurity } from '@disco/utils/password'
import ChangePassword from 'components/common/change-password'
import DocumentHeadTags from 'components/document-head-tags'
import WeakPasswordError from 'components/password/weak-password-error'
import Button from 'components/ui/button'
import Checkbox from 'components/ui/checkbox'
import TextField from 'components/ui/text-field'
import LoginError from 'components/utils/login-error'
import { withTranslation } from '@disco/i18n/with-translation'
import type { WithTranslation } from '@disco/i18n/with-translation'
import { Trans } from '@disco/i18n/trans'
import type { UserReducer } from '@disco/state-management/reducers/user'
import type { HttpErrorType } from 'types/errors'
type StateProps = {
  user: UserReducer
  passwordStrength: PasswordStrength
}
type OwnProps = object
type Actions = {
  loginUser: (
    loginDetails: LoginDetails,
    returnPath: string | false,
  ) => Promise<{ error?: HttpErrorType } | undefined>
  updateUserSettings: (settings: Partial<Settings>) => Promise<unknown>
  loadMfaMethods: () => void
}
type Validation = {
  getValidationMessages: (key: string) => {
    toString(): string
  }
  validate: (arg0?: (error: Error) => unknown) => void
}
type Props = StateProps &
  Actions &
  OwnProps &
  Validation &
  WithTranslation & { navigate: NavigateFunction }

type State = {
  loadingState: 'LOADING' | 'ERROR' | 'SUCCESS'
  submitting: boolean
  first_name: string
  last_name: string
  email: string
  password: string
  loadingError: HttpErrorType | null | undefined
  securityRequirementsError: React.ReactNode | null | undefined
  securityBreachError: boolean
  submitError: HttpErrorType | null | undefined
  marketingOptinCheckbox: boolean
}

const BREACHED_PASSWORD_ERROR = new Error('breached_password')

class PostInvites extends React.Component<Props, State> {
  state: State = {
    loadingState: 'LOADING',
    submitting: false,
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    loadingError: null,
    securityRequirementsError: null,
    securityBreachError: false,
    submitError: null,
    marketingOptinCheckbox: true,
  }
  validatorTypes = strategy.createSchema({
    first_name: 'required|max:30',
    last_name: 'max:30',
    // no need for password confirmation
    password: 'required',
  })

  componentDidMount() {
    this._loadUser()
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (this.state.loadingState === 'LOADING' && nextProps.user.is_active) {
      const { first_name, last_name, email } = nextProps.user
      this.setState({
        loadingState: 'SUCCESS',
        first_name,
        last_name,
        email,
      })
    } else if (this.state.loadingState === 'LOADING' && nextProps.user.error) {
      this.setState({
        loadingState: 'ERROR',
        loadingError: nextProps.user.error,
      })
    }
  }

  async _loadUser() {
    const loginDetails: LoginDetails = {
      referer: getReferrer(),
    }
    const returnPath = false // keep user here

    this.props.loginUser(loginDetails, returnPath)
  }

  getValidatorData = () => {
    const { first_name, last_name, password } = this.state
    return {
      first_name,
      last_name,
      password,
    }
  }
  _checkErrors = () => {
    const passwordSecure = checkPasswordSecurity(
      this.state.password,
      this.props.passwordStrength,
    )

    if (!passwordSecure) {
      this.setState({
        securityRequirementsError: (
          <WeakPasswordError passwordStrength={this.props.passwordStrength} />
        ),
      })
      return
    } else {
      this.setState({
        securityRequirementsError: null,
      })
    }

    this.props.validate()
  }
  _handleSubmit = async evt => {
    evt.stopPropagation()

    this.setState({
      submitError: null,
    })

    this._checkErrors()

    this.props.validate(async error => {
      if (error) return
      this.setState({
        submitting: true,
      })

      try {
        const {
          first_name,
          last_name,
          email,
          password,
          marketingOptinCheckbox,
        } = this.state

        await this.props.updateUserSettings({
          first_name,
          last_name,
          email,
          new_password: password,
          opt_in: marketingOptinCheckbox,
        })

        // TODO: check
        if (
          get(
            this.props.user,
            'business.private_metadata.mfa_auth_enabled',
            false,
          )
        ) {
          this.props.loadMfaMethods()
        } else {
          // Navigation will be handled by the container
          this.props.navigate('/')
        }
      } catch (err) {
        console.error(err)
        const breached = err?.body?.code === 'breached_password'
        this.setState({
          submitting: false,
          securityBreachError: breached,
          submitError: breached ? null : err,
        })
      }
    })
  }
  _handleInputChange = (
    evt: React.ChangeEvent<HTMLInputElement>,
    field: keyof State,
  ) => {
    this.setState(prevState => ({
      ...prevState,
      [field]: evt.target.value,
    }))
    this.props.validate()
  }

  _renderFields() {
    const { t } = this.props
    return (
      <div>
        <div className="PostInvites_Row">
          <TextField
            label={t('accounts:firstName', 'First Name')}
            placeholder={t('accounts:firstNamePlaceholder', 'John')}
            name="first_name"
            value={this.state.first_name}
            onChange={evt => this._handleInputChange(evt, 'first_name')}
            error={this.props.getValidationMessages('first_name').toString()}
            compact={false}
          />
        </div>
        <div className="PostInvites_Row">
          <TextField
            label={t('accounts:lastName', 'Last Name')}
            placeholder={t('accounts:lastNamePlaceholder', '(optional)')}
            name="last_name"
            value={this.state.last_name}
            onChange={evt => this._handleInputChange(evt, 'last_name')}
            error={this.props.getValidationMessages('last_name').toString()}
            compact={false}
          />
        </div>
        <div className="PostInvites_Row">
          <TextField
            label={t('accounts:emailAddress', 'Email')}
            placeholder={t('accounts:emailPlaceholder', 'axl@gnr.com')}
            name="last_name"
            value={this.state.email}
            onChange={evt => this._handleInputChange(evt, 'email')}
            error={this.props.getValidationMessages('email').toString()}
            readOnly
            compact={false}
          />
        </div>
        <div className="PostInvites_Row">
          <ChangePassword
            type="invite"
            passwordStrength={this.props.passwordStrength}
            onPasswordChange={() => {}}
            onInput={(key, password) => {
              this.setState(
                {
                  password,
                  securityBreachError: false,
                  submitError: null,
                },
                this._checkErrors,
              )
            }}
            validationOnInput
            passwordChangeError={
              this.state.securityBreachError ? BREACHED_PASSWORD_ERROR : null
            }
          />
        </div>
      </div>
    )
  }

  _renderSubmit() {
    const { securityRequirementsError, password } = this.state
    const { getValidationMessages, t } = this.props
    const submitEnabled =
      !securityRequirementsError &&
      password &&
      !getValidationMessages('first_name').toString() &&
      !getValidationMessages('last_name').toString() &&
      !getValidationMessages('email').toString()
    return (
      <Button
        disabled={!submitEnabled}
        className="PostInvites_SubmitBtn"
        large
        caption={t('accounts:createAccount', 'Create account')}
        appearance="primary"
        progressCaption={t(
          'accounts:creatingAccountProgress',
          'Creating account',
        )}
        progressIndicator={this.state.submitting}
        onClick={this._handleSubmit}
      />
    )
  }

  _renderSubmitError() {
    const { submitError } = this.state
    if (!submitError) return null
    return (
      <div className="PostInvites_Row">
        <LoginError
          passwordStrength={this.props.passwordStrength}
          error={submitError}
        />
      </div>
    )
  }

  render() {
    const { t } = this.props

    if (this.state.loadingState === 'LOADING') {
      return (
        <div className="PostInvites_Content">
          <h1 className="Form_Header PostInvites_Header">
            {t('accounts:createYourDiscoProfile', 'Create your DISCO profile')}
          </h1>
          {t('accounts:authorizing', 'Authorizing...')}
        </div>
      )
    }

    if (this.state.loadingState === 'ERROR') {
      return (
        <div className="PostInvites_Content">
          <h1 className="Form_Header PostInvites_Header">
            {t('accounts:createYourDiscoProfile', 'Create your DISCO profile')}
          </h1>
          {this.state.loadingError && (
            <LoginError
              passwordStrength={this.props.passwordStrength}
              errorContext="link"
              error={this.state.loadingError}
              suggestContactingSupport
            />
          )}
        </div>
      )
    }

    return (
      <div className="PostInvites_Content">
        <DocumentHeadTags
          title={t('accounts:createProfileDisco', 'Create profile | DISCO')}
        />
        <div className="PostInvites_BgContainer" />
        <div className="PostInvites_LogoContainer" />
        <div className="PostInvites_Container">
          <h1 className="Form_Header PostInvites_Header">
            {t('accounts:createYourDiscoProfile', 'Create your DISCO profile')}
          </h1>
          {this._renderFields()}
          <div className="PostInvites_SubmitWrap">
            <div className="PostInvites_TermsContainer">
              <Trans
                t={t}
                i18nKey="accounts:byClickingCreateYourAccountYouAgreeToBeBoundByOurTermsAndPrivacyPolicy"
              >
                By clicking “Create your account” you agree to be bound by our{' '}
                <a
                  className="PostInvites_Link"
                  href="https://disco.ac/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms
                </a>{' '}
                and{' '}
                <a
                  className="PostInvites_Link"
                  href="https://disco.ac/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </Trans>
            </div>
          </div>
          <div className="PostInvites_SubmitWrap PostInvites_SubmitWrap--Checkbox">
            <Checkbox
              checked={this.state.marketingOptinCheckbox}
              onChange={() => {
                this.setState({
                  marketingOptinCheckbox: !this.state.marketingOptinCheckbox,
                })
              }}
              label={
                <span className="PostInvites_Text">
                  {t(
                    'accounts:itsOkToEmailMeAboutTheDiscoService',
                    'It’s ok to email me about the DISCO service.',
                  )}
                </span>
              }
            />
          </div>
          {this._renderSubmitError()}
          {this._renderSubmit()}
        </div>
      </div>
    )
  }
}

const Wrapped = withTranslation('accounts')(
  validation(strategy)(PostInvites),
) as any

// HOC to provide navigate function to class component
const PostInvitesWithNavigate = props => {
  const navigate = useNavigate()
  return <Wrapped {...props} navigate={navigate} />
}

export default PostInvitesWithNavigate as any
